// src/modules/app-views/products/pages/Document/tabs/RecordTab/ProductInfoCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Col, Row, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleCascader from 'components/SimpleComponents/SimpleCascader';
import useZustand from 'hooks/useZustand';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ProductInfoCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');
  const storedModule = useZustand('module', 'config');
  const { _id: documentId } = useDocumentContext() || {};
  const { form } = useSimpleFormContext() || {};

  // Watch current values to show removed items if product is using them
  const currentBrandUid = Form.useWatch('brandUid', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const brandsOptions = useMemo(
    () => buildBrandsOptions(storedParameters, storedModule?.id, documentId, currentBrandUid),
    [storedParameters, storedModule?.id, documentId, currentBrandUid]
  );
  const categoriesOptions = useMemo(
    () => buildCategoriesOptions(storedParameters, storedModule?.id),
    [storedParameters, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={<div className="text-muted">{translateX('additional_info')}</div>}>
      {/* Line 1: DESCRIPTION, BRAND */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={14} lg={14}>
          <SimpleInput name="description" label="description" />
        </Col>

        <Col xs={24} sm={10} lg={10}>
          <SimpleSelect
            name="brandUid"
            label="brand"
            allowClear
            showSearch
            options={brandsOptions}
            showAddButton={{ modal: 'parameters', tab: 'brands' }}
          />
        </Col>
      </Row>

      {/* Line 2: CATEGORY, EAN */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={14} lg={14}>
          <SimpleCascader
            name="categoryUids"
            label="category"
            options={categoriesOptions}
            placeholder="select_category"
            showAddButton={{ modal: 'parameters', tab: 'categories' }}
          />
        </Col>

        <Col xs={24} sm={10} lg={10}>
          <SimpleInput name="ean" label="ean" />
        </Col>
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ProductInfoCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildBrandsOptions(storedParameters, moduleId, documentId, currentBrandUid) {
  const brandsArray = storedParameters?.modules?.[moduleId]?.brands;

  if (!brandsArray || !Array.isArray(brandsArray)) return [];

  // Filter brands:
  // - Show all active brands (removed !== true)
  // - Show removed brands ONLY if the current product is using it
  const isNewDocument = !documentId || documentId === 'add';

  const filteredBrands = brandsArray.filter(item => {
    if (!item.removed) return true;
    if (isNewDocument) return false;
    return item.uid === currentBrandUid;
  });

  return filteredBrands.map(item => {
    const label = item.label || '';

    return {
      value: item.uid,
      text: label,
      label: label,
      disabled: false,
    };
  });
}

function buildCategoriesOptions(storedParameters, moduleId) {
  const rawCategories = storedParameters?.modules?.[moduleId]?.categories;

  if (!rawCategories || !Array.isArray(rawCategories) || rawCategories.length === 0) {
    return [];
  }

  // Transform categories to Cascader format (value, label, children)
  return transformCategoriesToCascader(rawCategories);
}

function transformCategoriesToCascader(categories) {
  if (!categories || !Array.isArray(categories)) return [];

  return categories.map(cat => ({
    value: cat.uid,
    label: cat.label,
    children: cat.children?.length > 0 ? transformCategoriesToCascader(cat.children) : undefined,
  }));
}
